export const GEN_STATUS_OPTIONS = [
    { value: "draft", label: "草稿" },
    { value: "preparing", label: "处理中" },
    { value: "generating", label: "生成中" },
    { value: "done", label: "已完成" },
    { value: "failed", label: "失败" },
];

export const PUBLISH_STATUS_OPTIONS = [
    { value: "unpublished", label: "未发布" },
    { value: "published", label: "已发布" },
];

/** 数据维度固定顺序，与后端 RptV2Codes.DATA_SOURCE_ORDER 一致 */
export const SOURCE_ORDER = ["gsq", "homework", "log", "survey", "report", "attendance"];

export const SOURCE_LABELS = {
    gsq: "观思问",
    homework: "作业",
    log: "日志",
    survey: "调研问卷",
    report: "报告",
    attendance: "考勤",
};

const GEN_TAG = {
    draft: "info",
    preparing: "warning",
    generating: "warning",
    done: "success",
    failed: "danger",
};

const MEMBER_STATUS = {
    pending: "待生成",
    running: "生成中",
    done: "已完成",
    failed: "失败",
};

export const genStatusLabel = (value, fallback) => {
    if (fallback) return fallback;
    const found = GEN_STATUS_OPTIONS.find(item => item.value === value);
    return found ? found.label : (value || "—");
};

export const genStatusTag = (value) => GEN_TAG[value] || "info";

export const publishStatusLabel = (value, fallback) => {
    if (fallback) return fallback;
    const found = PUBLISH_STATUS_OPTIONS.find(item => item.value === value);
    return found ? found.label : (value || "未发布");
};

export const memberStatusLabel = (value, fallback) => {
    if (fallback) return fallback;
    return MEMBER_STATUS[value] || value || "—";
};

export const parseScope = (scope) => {
    if (!scope) return {};
    if (typeof scope === "object") return scope;
    try {
        return JSON.parse(scope);
    } catch (e) {
        return {};
    }
};

const namesByIds = (list, ids) => {
    if (!ids || !ids.length) return "";
    const map = {};
    (list || []).forEach(item => {
        map[String(item.id)] = item.name;
    });
    return ids.map(id => map[String(id)] || id).join("、");
};

/** 勾选值与选项 id 对齐，避免数字和字符串对不上导致回显为空 */
const alignIds = (selected, list) => {
    const raw = selected || [];
    if (!list || !list.length) return raw.slice();
    const byKey = {};
    list.forEach(item => {
        byKey[String(item.id)] = item.id;
    });
    return raw.map(id => (byKey[String(id)] !== undefined ? byKey[String(id)] : id));
};

/**
 * 解析数据维度。旧报告没有 dataSources 时按主数据维度兜底：homework 归作业，其余归观思问。
 */
export const resolveSources = (scopeRaw, dataType) => {
    const scope = parseScope(scopeRaw);
    const picked = Array.isArray(scope.dataSources) && scope.dataSources.length
        ? scope.dataSources
        : [dataType === "homework" ? "homework" : "gsq"];
    const codes = picked.map(code => (code === "gsw" ? "gsq" : code));
    return SOURCE_ORDER.filter(code => codes.indexOf(code) >= 0);
};

const scopeSourceText = (scope, opt, code) => {
    if (code === "gsq") {
        if (scope.gsqMode === "dates") {
            return `观思问·${(scope.gsqDates || []).length}个问卷日期`;
        }
        if (scope.gsqMode === "date") {
            return `观思问·自定义日期 ${scope.dateStart || "未选"} ~ ${scope.dateEnd || "未选"}`;
        }
        return `观思问·${namesByIds(opt.cycles, scope.cycleIds) || "全部周期"}`;
    }
    if (code === "homework") {
        return `作业·${namesByIds(opt.homeworks, scope.homeworkIds) || "全部"}`;
    }
    if (code === "log") {
        if (scope.logMode === "dates") return `日志·${(scope.logDates || []).length}个作答日期`;
        if (scope.logStart || scope.logEnd) return `日志·${scope.logStart || "?"} ~ ${scope.logEnd || "?"}`;
        return "日志·全部作答日";
    }
    if (code === "survey") {
        const count = (scope.surveyIds || []).length;
        return count ? `调研问卷×${count}` : "调研问卷";
    }
    if (code === "report") {
        const count = (scope.reportIds || []).length;
        return count ? `报告×${count}` : "报告";
    }
    if (code === "attendance") {
        if (scope.attendanceMode === "dates") return `考勤·${(scope.attendanceDates || []).length}个日期`;
        if (scope.attendanceStart || scope.attendanceEnd) return `考勤·${scope.attendanceStart || "?"} ~ ${scope.attendanceEnd || "?"}`;
        return "考勤·全部日期";
    }
    return "";
};

/** 只渲染数据维度摘要，向导第4步和列表复用 */
export const scopeSourceLabel = (scopeRaw, options, dataType) => {
    const scope = parseScope(scopeRaw);
    const opt = options || {};
    return resolveSources(scope, dataType)
        .map(code => scopeSourceText(scope, opt, code))
        .filter(Boolean)
        .join(" + ");
};

export const formatScope = (scopeRaw, options, dataType) => {
    const scope = parseScope(scopeRaw);
    const opt = options || {};
    const stageNames = namesByIds(opt.stages, scope.stageIds);
    const groupNames = namesByIds(opt.userGroups, scope.userGroupIds);
    const areaText = !scope.areas || !scope.areas.length || scope.areas.indexOf("全部") >= 0
        ? "全部区域"
        : scope.areas.join("、");
    return [
        stageNames || "全部阶段",
        areaText,
        groupNames,
        scopeSourceLabel(scope, opt, dataType),
    ].filter(Boolean).join(" · ");
};

/** 表单态下每个维度的范围摘要，用于已选维度条 */
export const formSourceLabel = (form, options, code) => {
    const target = form || {};
    const opt = options || {};
    if (code === "gsq") {
        if (target.gsqMode === "dates") {
            const count = (target.gsqDates || []).length;
            return count ? `${count}个问卷日期` : "未选问卷日期";
        }
        if (target.gsqMode === "date") {
            return `自定义日期 ${target.dateStart || "未选"} ~ ${target.dateEnd || "未选"}`;
        }
        if (target.cycleAll) return "全部周期";
        return namesByIds(opt.cycles, target.cycleIds) || "未选周期";
    }
    if (code === "homework") {
        if (target.homeworkAll) return "全部作业";
        return namesByIds(opt.homeworks, target.homeworkIds) || "未选作业";
    }
    if (code === "log") {
        if (target.logMode === "dates") {
            const count = (target.logDates || []).length;
            return count ? `${count}个作答日期` : "未选作答日期";
        }
        if (!target.logStart && !target.logEnd) return "全部作答日";
        return `${target.logStart || "?"} ~ ${target.logEnd || "?"}`;
    }
    if (code === "survey") {
        const count = (target.surveyIds || []).length;
        return count ? `${count}份问卷` : "未选问卷";
    }
    if (code === "report") {
        const count = (target.reportIds || []).length;
        return count ? `${count}份报告` : "未选报告";
    }
    if (code === "attendance") {
        if (target.attendanceMode === "dates") {
            const count = (target.attendanceDates || []).length;
            return count ? `${count}个日期` : "未选日期";
        }
        if (!target.attendanceStart && !target.attendanceEnd) return "全部日期";
        return `${target.attendanceStart || "?"} ~ ${target.attendanceEnd || "?"}`;
    }
    return "";
};

export const emptyScopeForm = () => ({
    stageAll: true,
    stageIds: [],
    groupAll: true,
    userGroupIds: [],
    areaAll: true,
    areas: [],
    dataSources: ["gsq"],
    focusType: "gsq",
    gsqMode: "date",
    cycleAll: true,
    cycleIds: [],
    dateStart: "",
    dateEnd: "",
    gsqDates: [],
    homeworkAll: true,
    homeworkIds: [],
    logMode: "date",
    logStart: "",
    logEnd: "",
    logDates: [],
    surveyIds: [],
    reportIds: [],
    attendanceMode: "date",
    attendanceStart: "",
    attendanceEnd: "",
    attendanceDates: [],
});

const coversAll = (selected, list) => {
    if (!selected || !selected.length) return true;
    if (!list || !list.length) return false;
    return selected.length >= list.length;
};

export const scopeToForm = (scopeRaw, options, dataType) => {
    const scope = parseScope(scopeRaw);
    const opt = options || {};
    const stageIds = scope.stageIds || [];
    const userGroupIds = scope.userGroupIds || [];
    const cycleIds = scope.cycleIds || [];
    const homeworkIds = scope.homeworkIds || [];
    const areas = scope.areas || [];
    const areaAll = !areas.length || areas.indexOf("全部") >= 0;
    const sources = resolveSources(scope, dataType);
    return {
        stageAll: coversAll(stageIds, opt.stages),
        stageIds: alignIds(stageIds, opt.stages),
        groupAll: coversAll(userGroupIds, opt.userGroups),
        userGroupIds: alignIds(userGroupIds, opt.userGroups),
        areaAll,
        areas: areaAll ? [] : areas.slice(),
        dataSources: sources,
        focusType: sources[0] || "gsq",
        gsqMode: scope.gsqMode === "cycle" ? "cycle" : scope.gsqMode === "dates" ? "dates" : "date",
        cycleAll: scope.gsqMode === "cycle"
            && coversAll(cycleIds, filteredCycles(opt.cycles, stageIds, coversAll(stageIds, opt.stages))),
        cycleIds: alignIds(cycleIds, opt.cycles),
        dateStart: scope.dateStart || "",
        dateEnd: scope.dateEnd || "",
        gsqDates: (scope.gsqDates || []).slice(),
        homeworkAll: !homeworkIds.length,
        homeworkIds: alignIds(homeworkIds, opt.homeworks),
        logMode: scope.logMode === "dates" ? "dates" : "date",
        logStart: scope.logStart || "",
        logEnd: scope.logEnd || "",
        logDates: (scope.logDates || []).slice(),
        surveyIds: alignIds(scope.surveyIds || [], opt.surveys),
        reportIds: alignIds(scope.reportIds || [], opt.reports),
        attendanceMode: scope.attendanceMode === "dates" ? "dates" : "date",
        attendanceStart: scope.attendanceStart || "",
        attendanceEnd: scope.attendanceEnd || "",
        attendanceDates: (scope.attendanceDates || []).slice(),
    };
};

const filteredCycles = (cycles, stageIds, stageAll) => {
    if (stageAll) return cycles || [];
    const keys = {};
    (stageIds || []).forEach(id => {
        keys[String(id)] = true;
    });
    return (cycles || []).filter(item => keys[String(item.stageId)]);
};

const idsOrAll = (isAll, selected, list) => {
    if (isAll) return (list || []).map(item => item.id);
    return selected || [];
};

/**
 * 表单转 scope。未选维度的字段写空值，保证同一配置的 JSON 稳定。
 */
export const formToScope = (form, options) => {
    const opt = options || {};
    const sources = SOURCE_ORDER.filter(code => (form.dataSources || []).indexOf(code) >= 0);
    const has = code => sources.indexOf(code) >= 0;
    const gsqRange = has("gsq") && form.gsqMode === "date";
    const gsqPick = has("gsq") && form.gsqMode === "dates";
    const gsqCycle = has("gsq") && form.gsqMode === "cycle";
    const logDates = has("log") && form.logMode === "dates";
    const attendanceDates = has("attendance") && form.attendanceMode === "dates";
    return {
        stageIds: idsOrAll(form.stageAll, form.stageIds, opt.stages),
        userGroupIds: idsOrAll(form.groupAll, form.userGroupIds, opt.userGroups),
        areas: form.areaAll ? [] : (form.areas || []),
        dataSources: sources,
        gsqMode: has("gsq") ? form.gsqMode : null,
        cycleIds: gsqCycle
            ? idsOrAll(form.cycleAll, form.cycleIds, filteredCycles(opt.cycles, form.stageIds, form.stageAll))
            : [],
        dateStart: gsqRange ? (form.dateStart || null) : null,
        dateEnd: gsqRange ? (form.dateEnd || null) : null,
        gsqDates: gsqPick ? (form.gsqDates || []) : [],
        homeworkIds: has("homework") && !form.homeworkAll ? (form.homeworkIds || []) : [],
        logMode: has("log") ? (logDates ? "dates" : "date") : null,
        logStart: has("log") && !logDates ? (form.logStart || null) : null,
        logEnd: has("log") && !logDates ? (form.logEnd || null) : null,
        logDates: logDates ? (form.logDates || []) : [],
        surveyIds: has("survey") ? (form.surveyIds || []) : [],
        reportIds: has("report") ? (form.reportIds || []) : [],
        attendanceMode: has("attendance") ? (attendanceDates ? "dates" : "date") : null,
        attendanceStart: has("attendance") && !attendanceDates ? (form.attendanceStart || null) : null,
        attendanceEnd: has("attendance") && !attendanceDates ? (form.attendanceEnd || null) : null,
        attendanceDates: attendanceDates ? (form.attendanceDates || []) : [],
    };
};
